import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, AlertTriangle, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { el } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DeadlinePreview Component
 * Εμφανίζει προεπισκόπηση των υπολογισμένων προθεσμιών πριν την υποβολή
 */
const DeadlinePreview = ({ 
  templateId, 
  baseDate, 
  selectedDeadlineIds, 
  options = {},
  hearingDate = null,
  visible = false 
}) => {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = parseISO(dateStr);
      return format(date, 'd MMMM yyyy', { locale: el });
    } catch {
      return dateStr;
    }
  };

  // Calculate deadlines when inputs change
  const calculateDeadlines = useCallback(async () => {
    if (!templateId || !baseDate || selectedDeadlineIds.length === 0) {
      setDeadlines([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestBody = {
        template_id: templateId,
        base_date: baseDate,
        selected_deadline_ids: selectedDeadlineIds,
        options: options || {},
        hearing_date: hearingDate
      };

      const response = await axios.post(`${API}/calculate-from-template`, requestBody);
      setDeadlines(response.data.deadlines || []);
    } catch (err) {
      console.error('Error calculating deadlines:', err);
      setError('Σφάλμα υπολογισμού προθεσμιών');
      setDeadlines([]);
    } finally {
      setLoading(false);
    }
  }, [templateId, baseDate, selectedDeadlineIds, options, hearingDate]);

  // Debounce the calculation
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateDeadlines();
    }, 300);
    return () => clearTimeout(timer);
  }, [calculateDeadlines]);

  // Don't render if not visible or no selections
  if (!visible || selectedDeadlineIds.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden" data-testid="deadline-preview">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-blue-100/50 hover:bg-blue-100 transition-colors"
        data-testid="deadline-preview-toggle"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-red-700" />
          <span className="text-sm font-semibold text-slate-800">
            Προεπισκόπηση προθεσμιών
          </span>
          {deadlines.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded-full">
              {deadlines.length}
            </span>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-red-700" />
        ) : (
          <ChevronDown className="w-4 h-4 text-red-700" />
        )}
      </button>

      {/* Content */}
      {expanded && (
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center py-4" data-testid="deadline-preview-loading">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent mr-2"></div>
              <span className="text-sm text-red-700">Υπολογισμός...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="deadline-preview-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          ) : deadlines.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">
              Επιλέξτε προθεσμίες για να δείτε την προεπισκόπηση
            </p>
          ) : (
            <div className="space-y-2">
              {deadlines.map((deadline, index) => (
                <div
                  key={deadline.id || index}
                  className="flex items-start gap-3 p-3 bg-white rounded-lg border border-blue-100 shadow-sm"
                  data-testid={`deadline-preview-item-${deadline.id}`}
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-red-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {deadline.name}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="w-3 h-3 text-slate-400" />
                      <p className="text-xs font-semibold text-red-700">
                        {formatDate(deadline.deadline_date)}
                      </p>
                    </div>
                    {deadline.article && (
                      <p className="text-xs text-slate-500 mt-1">
                        {deadline.article}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Info note */}
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-100 rounded text-xs text-amber-700 mt-3">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>
                  Οι ημερομηνίες υπολογίζονται αυτόματα με βάση τις δικαστικές διακοπές και αργίες
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeadlinePreview;
